import { useCallback, useEffect, useState } from "react";
import { Button, useTheme } from "@mui/material";
import cn from "../../utils/cn";
import {
  canEditAIPlan,
  canGenerateAIPlan,
  fetchAIPlan,
  generateAIPlan,
  getAIErrorCode,
  getAIErrorMessage,
  planValidationErrors,
  saveAIPlan,
} from "../../utils/aiInterviewApi";
import { BulletList, ErrorBox, LoadingLine, SECTION_LABELS, humanize, toText } from "./aiShared";

const linesOf = (items) => (Array.isArray(items) ? items.map(toText).join("\n") : "");
const listOf = (text) => text.split("\n").map((line) => line.trim()).filter(Boolean);

// Rubric, follow-up probes and easier/harder variants for a plan question (also used by the live dashboard)
export const PlanQuestionDetails = ({ question }) => {
  const rubric = question.rubric || {};
  const groups = [
    ["Important points", rubric.important_points],
    ["Expected points", rubric.expected_points],
    ["Follow-up probes", question.follow_up_probes],
  ].filter(([, items]) => Array.isArray(items) && items.length);
  const variants = [
    ["Easier", question.easier_variant],
    ["Harder", question.harder_variant],
  ].filter(([, text]) => text);

  if (!groups.length && !variants.length) return <p className="text-xs text-gray-500">No rubric for this question.</p>;
  return (
    <div className="flex flex-col gap-2 text-sm">
      {groups.map(([label, items]) => (
        <div key={label}>
          <p className="text-xs font-medium">{label}</p>
          <BulletList items={items} />
        </div>
      ))}
      {variants.map(([label, text]) => (
        <p key={label} className="text-sm">
          <span className="text-xs font-medium">{label}: </span>
          {toText(text)}
        </p>
      ))}
    </div>
  );
};

export const questionMeta = (question) =>
  [
    toText(question.skill),
    humanize(toText(question.difficulty)),
    question.priority ? humanize(toText(question.priority)) : "",
    question.is_spare ? "Spare" : "",
    question.est_minutes ? `~${question.est_minutes} min` : "",
  ].filter(Boolean).join(" · ");

const TextArea = ({ id, label, value, onChange, rows = 2 }) => {
  const theme = useTheme();
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium mb-1">{label}</label>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        className="w-full px-3 py-2 border rounded-md text-sm"
        style={{ borderColor: theme.palette.secondary.v4, color: "#111111", backgroundColor: "#ffffff" }}
      />
    </div>
  );
};

const QuestionEditor = ({ question, onChange }) => {
  const id = `plan-${question.question_id}`;
  const rubric = question.rubric || {};
  const setRubric = (key, text) => onChange({ rubric: { ...rubric, [key]: listOf(text) } });
  return (
    <div className="flex flex-col gap-2">
      <TextArea id={`${id}-text`} label="Question" value={question.text || ""} onChange={(text) => onChange({ text })} />
      <TextArea
        id={`${id}-important`}
        label="Important points (one per line)"
        value={linesOf(rubric.important_points)}
        onChange={(text) => setRubric("important_points", text)}
        rows={3}
      />
      <TextArea
        id={`${id}-expected`}
        label="Expected points (one per line)"
        value={linesOf(rubric.expected_points)}
        onChange={(text) => setRubric("expected_points", text)}
        rows={3}
      />
      <TextArea
        id={`${id}-probes`}
        label="Follow-up probes (one per line)"
        value={linesOf(question.follow_up_probes)}
        onChange={(text) => onChange({ follow_up_probes: listOf(text) })}
        rows={3}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <TextArea
          id={`${id}-easier`}
          label="Easier variant"
          value={question.easier_variant || ""}
          onChange={(text) => onChange({ easier_variant: text.trim() ? text : null })}
        />
        <TextArea
          id={`${id}-harder`}
          label="Harder variant"
          value={question.harder_variant || ""}
          onChange={(text) => onChange({ harder_variant: text.trim() ? text : null })}
        />
      </div>
    </div>
  );
};

const PlanQuestion = ({ question, index, editing, onChange }) => {
  const theme = useTheme();
  return (
    <li
      className="rounded-lg border p-3 flex flex-col gap-2"
      style={{ borderColor: theme.palette.secondary.v4, backgroundColor: theme.palette.primary.main }}
    >
      {editing ? (
        <QuestionEditor question={question} onChange={onChange} />
      ) : (
        <details>
          <summary className="cursor-pointer list-none flex flex-col gap-1">
            <span className="text-sm font-medium">
              <span className="text-gray-500">{index + 1}. </span>
              {toText(question.text)}
            </span>
            <span className="text-xs text-gray-500">
              {questionMeta(question)}
              <span className="ml-2 text-blue-700">Details</span>
            </span>
          </summary>
          <div className="mt-2 pl-4 border-l-2 border-gray-200">
            <PlanQuestionDetails question={question} />
          </div>
        </details>
      )}
    </li>
  );
};

// The AI's question plan (both AI modes). Editable only while the session is READY.
export default function AIPlanView({ interviewId, status, onPlanChanged }) {
  const theme = useTheme();
  const [plan, setPlan] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [invalid, setInvalid] = useState([]);
  const muted = { color: theme.palette.text.secondary || "#6b7280" };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      setPlan(await fetchAIPlan(interviewId));
    } catch (err) {
      setLoadError(getAIErrorMessage(err, "Couldn't load the questions."));
    } finally {
      setLoading(false);
    }
  }, [interviewId]);

  useEffect(() => {
    setDraft(null);
    setActionError("");
    setInvalid([]);
    load();
  }, [load]);

  const editable = canEditAIPlan(status);
  const editing = Boolean(draft) && editable;

  const handleGenerate = async () => {
    setBusy(true);
    setActionError("");
    try {
      setPlan(await generateAIPlan(interviewId));
      onPlanChanged?.();
    } catch (err) {
      const message = getAIErrorMessage(err, "Couldn't generate the questions.");
      setActionError(getAIErrorCode(err) === "LLM_PROVIDER_ERROR" ? `${message} Try again.` : message);
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    setBusy(true);
    setActionError("");
    setInvalid([]);
    try {
      const saved = await saveAIPlan(interviewId, draft);
      setPlan(saved || draft);
      setDraft(null);
      onPlanChanged?.();
    } catch (err) {
      if (getAIErrorCode(err) === "PLAN_INVALID") setInvalid(planValidationErrors(err));
      setActionError(getAIErrorMessage(err, "Couldn't save the questions."));
    } finally {
      setBusy(false);
    }
  };

  const updateQuestion = (sectionIndex, questionIndex, patch) => {
    setDraft((previous) => ({
      ...previous,
      sections: previous.sections.map((section, sIndex) =>
        sIndex !== sectionIndex
          ? section
          : {
            ...section,
            questions: section.questions.map((question, qIndex) =>
              qIndex === questionIndex ? { ...question, ...patch } : question
            ),
          }
      ),
    }));
  };

  if (loading) return <LoadingLine>Loading questions...</LoadingLine>;
  if (loadError) return <ErrorBox onRetry={load}>{loadError}</ErrorBox>;

  if (!plan) {
    return (
      <div className="flex flex-col gap-3 items-start">
        <p className="text-sm">Questions are prepared up to 24 h before the interview.</p>
        {canGenerateAIPlan(status) && (
          <Button
            onClick={handleGenerate}
            disabled={busy}
            sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none" }}
          >
            {busy ? "Generating... (up to a minute)" : "Generate now"}
          </Button>
        )}
        {actionError && <ErrorBox>{actionError}</ErrorBox>}
      </div>
    );
  }

  const shown = editing ? draft : plan;
  const sections = Array.isArray(shown.sections) ? shown.sections : [];

  return (
    <div className="flex flex-col gap-4">
      {shown.round?.focus && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex flex-col gap-1">
          <p className="text-sm font-semibold">Why these questions{shown.round.name ? ` · ${toText(shown.round.name)}` : ""}</p>
          <p className="text-sm whitespace-pre-line">{toText(shown.round.focus)}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs" style={muted}>
          {[
            shown.version ? `Version ${shown.version}` : "",
            shown.source === "edited" ? "Edited" : shown.source ? "Generated" : "",
            editable ? "" : "Questions can be edited only while the AI is ready, before the interview starts.",
          ].filter(Boolean).join(" · ")}
        </p>
        {editable && (
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button
                  onClick={() => {
                    setDraft(null);
                    setActionError("");
                    setInvalid([]);
                  }}
                  disabled={busy}
                  sx={{ color: theme.palette.text.main, backgroundColor: "transparent", textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={busy}
                  sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none" }}
                >
                  {busy ? "Saving..." : "Save questions"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setDraft(JSON.parse(JSON.stringify(plan)))}
                sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none" }}
              >
                Edit questions
              </Button>
            )}
          </div>
        )}
      </div>

      {actionError && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm" role="alert">
          {actionError}
          {invalid.length > 0 && (
            <ul className="list-disc pl-5 mt-1">
              {invalid.map((item, index) => <li key={index}>{item}</li>)}
            </ul>
          )}
        </div>
      )}

      {shown.plan_warnings?.length > 0 && (
        <div className="p-3 rounded border border-amber-300 bg-amber-50 text-amber-900">
          <BulletList items={shown.plan_warnings} />
        </div>
      )}

      {sections.length === 0 && <p className="text-sm">The plan has no questions.</p>}
      {sections.map((section, sectionIndex) => (
        <section key={section.section_id || sectionIndex} className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">
            {toText(section.name) || SECTION_LABELS[section.section_id] || humanize(section.section_id)}
            <span className="font-normal" style={muted}>
              {[
                section.duration_minutes ? ` · ${section.duration_minutes} min` : "",
                section.questions?.length ? ` · ${section.questions.length} questions` : "",
              ].join("")}
            </span>
          </h3>
          <ol className={cn("flex flex-col gap-2")}>
            {(section.questions || []).map((question, questionIndex) => (
              <PlanQuestion
                key={question.question_id || questionIndex}
                question={question}
                index={questionIndex}
                editing={editing}
                onChange={(patch) => updateQuestion(sectionIndex, questionIndex, patch)}
              />
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
